import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Box, Grid, TextField, Typography, Button, Divider, MenuItem, Select,
    FormControl, InputLabel, Paper, CircularProgress, Snackbar, InputAdornment,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Tooltip, Chip, Pagination, Stack, Dialog, DialogTitle,
    DialogContent, DialogActions
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { format } from 'date-fns';
import api from '../../services/api';
import * as qs from 'qs';
import { maskCNPJ } from '../../utils/mask';

const PAYMENT_METHODS = [
    'Dinheiro',
    'PIX',
    'Cheque',
    'Transferência / Depósito',
    'Cartão de Crédito',
    'Cartão de Débito',
];

const emptyForm = {
    // Pagador
    payerName: '',
    payerDoc: '',
    // Referente
    referente: '',
    // Valor
    value: '',
    // Emissor
    emitterDoc: '',
    emitterName: '',
    emitterPhone: '',
    emitterCity: '',
    // Pagamento
    paymentMethod: 'Dinheiro',
    paymentDate: format(new Date(), 'yyyy-MM-dd'),
    // Extra
    observations: '',
};

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function formatCurrency(val) {
    const num = parseFloat(val.toString().replace(/[^\d,]/g, '').replace(',', '.'));
    if (isNaN(num)) return val;
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function Recibos() {
    const [form, setForm] = useState(emptyForm);
    const [searching, setSearching] = useState(false);
    const [saving, setSaving] = useState(false);
    const [alert, setAlert] = useState({ open: false, type: 'success', msg: '' });
    const receiptRef = useRef();

    // Lista de recibos salvos
    const [receipts, setReceipts] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [pages, setPages] = useState({ pageCount: 1 });
    const currentPage = useRef(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });

    // Gera número do recibo baseado em timestamp
    const receiptNumber = useRef(Date.now().toString().slice(-6));

    const handleField = (field) => (e) => {
        setForm(prev => ({ ...prev, [field]: e.target.value }));
    };

    const handleCNPJChange = (e) => {
        const formatted = maskCNPJ(e.target.value);
        setForm(prev => ({ ...prev, emitterDoc: formatted }));
        if (formatted.replace(/\D/g, '').length === 14) {
            searchByCNPJ(formatted);
        }
    };

    const searchByCNPJ = async (doc) => {
        setSearching(true);
        try {
            const query = qs.stringify({
                filters: { document: { $eq: doc } },
                populate: '*',
                encodeValuesOnly: true,
            });
            const response = await api.get('/companies?' + query);
            const data = response.data.data;
            if (data && data.length > 0) {
                const c = data[0].attributes;
                setForm(prev => ({
                    ...prev,
                    emitterName: c.social_reason || c.trade || '',
                    emitterPhone: c.phone || '',
                    emitterCity: c.city || '',
                }));
                setAlert({ open: true, type: 'success', msg: 'Dados do emissor preenchidos automaticamente.' });
            } else {
                setAlert({ open: true, type: 'warning', msg: 'CNPJ não encontrado no cadastro.' });
            }
        } catch (err) {
            setAlert({ open: true, type: 'error', msg: 'Erro ao buscar CNPJ.' });
        } finally {
            setSearching(false);
        }
    };

    // ─── Carregar lista de recibos ─── //
    const loadReceipts = useCallback(async (page = 1, search = '') => {
        setLoadingList(true);
        try {
            const filters = {};
            if (search.trim()) {
                filters.$or = [
                    { payer_name: { $containsi: search } },
                    { emitter_name: { $containsi: search } },
                    { emitter_doc: { $containsi: search } },
                    { receipt_number: { $containsi: search } },
                ];
            }
            const query = qs.stringify({
                filters,
                sort: ['createdAt:desc'],
                pagination: { pageSize: 10, page },
                encodeValuesOnly: true,
            });
            const response = await api.get('/receipts?' + query);
            setReceipts(response.data.data || []);
            setPages(response.data.meta.pagination);
        } catch (err) {
            setAlert({ open: true, type: 'error', msg: 'Erro ao carregar recibos.' });
        } finally {
            setLoadingList(false);
        }
    }, []);

    useEffect(() => { loadReceipts(); }, [loadReceipts]);

    // ─── Salvar recibo ───
    const handleSave = async () => {
        if (!form.payerName || !form.value || !form.referente || !form.emitterName) {
            setAlert({ open: true, type: 'warning', msg: 'Preencha os campos obrigatórios: Pagador, Valor, Referente e Emissor.' });
            return;
        }
        setSaving(true);
        try {
            const payload = {
                receipt_number: receiptNumber.current,
                payer_name: form.payerName,
                payer_doc: form.payerDoc || null,
                referente: form.referente,
                value: parseFloat(form.value.toString().replace(/[^\d,]/g, '').replace(',', '.')) || 0,
                emitter_doc: form.emitterDoc || null,
                emitter_name: form.emitterName,
                emitter_phone: form.emitterPhone || null,
                emitter_city: form.emitterCity || null,
                payment_method: form.paymentMethod,
                payment_date: form.paymentDate,
                observations: form.observations || null,
            };
            await api.post('/receipts', { data: payload });
            setAlert({ open: true, type: 'success', msg: `Recibo Nº ${receiptNumber.current} salvo com sucesso!` });
            receiptNumber.current = Date.now().toString().slice(-6);
            loadReceipts(currentPage.current, searchQuery);
        } catch (err) {
            setAlert({ open: true, type: 'error', msg: 'Erro ao salvar recibo.' });
        } finally {
            setSaving(false);
        }
    };

    // ─── Carregar recibo salvo no formulário ───
    const handleLoad = (row) => {
        const a = row.attributes;
        receiptNumber.current = a.receipt_number;
        setForm({
            payerName: a.payer_name || '',
            payerDoc: a.payer_doc || '',
            referente: a.referente || '',
            value: a.value !== null && a.value !== undefined ? String(a.value).replace('.', ',') : '',
            emitterDoc: a.emitter_doc || '',
            emitterName: a.emitter_name || '',
            emitterPhone: a.emitter_phone || '',
            emitterCity: a.emitter_city || '',
            paymentMethod: a.payment_method || 'Dinheiro',
            paymentDate: a.payment_date || format(new Date(), 'yyyy-MM-dd'),
            observations: a.observations || '',
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // ─── Excluir recibo ───
    const handleDelete = async (id) => {
        try {
            await api.delete(`/receipts/${id}`);
            setAlert({ open: true, type: 'success', msg: 'Recibo excluído.' });
            loadReceipts(currentPage.current, searchQuery);
        } catch {
            setAlert({ open: true, type: 'error', msg: 'Erro ao excluir recibo.' });
        } finally {
            setDeleteConfirm({ open: false, id: null });
        }
    };

    const handlePrint = () => {
        const printContents = receiptRef.current.innerHTML;
        const win = window.open('', '_blank', 'width=900,height=700');
        win.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8" />
                <title>Recibo</title>
                <style>
                    * { box-sizing: border-box; margin: 0; padding: 0; }
                    body { font-family: Arial, sans-serif; font-size: 13px; color: #222; background: #fff; }
                    .receipt-wrap { width: 100%; padding: 30px; }
                    .receipt-header { text-align: center; border-bottom: 2px solid #222; padding-bottom: 10px; margin-bottom: 14px; }
                    .receipt-header h2 { font-size: 22px; letter-spacing: 4px; text-transform: uppercase; }
                    .receipt-value-box { border: 2px solid #222; padding: 8px 18px; display: inline-block; font-size: 20px; font-weight: bold; float: right; margin-bottom: 8px; }
                    .receipt-body { line-height: 2; }
                    .receipt-body span.underline { border-bottom: 1px solid #555; display: inline-block; min-width: 260px; padding: 0 4px; }
                    .receipt-footer { margin-top: 30px; }
                    .receipt-footer .signature-line { border-top: 1px solid #222; width: 320px; margin: 0 auto; text-align: center; padding-top: 6px; font-size: 12px; }
                    .divider { border: none; border-top: 1px dashed #aaa; margin: 20px 0; }
                    .obs-block { margin-top: 12px; font-size: 12px; color: #444; }
                    .clearfix::after { content: ""; display: table; clear: both; }
                    @media print { body { -webkit-print-color-adjust: exact; } }
                </style>
            </head>
            <body>${printContents}</body>
            </html>
        `);
        win.document.close();
        win.focus();
        setTimeout(() => { win.print(); win.close(); }, 400);
    };

    const handleClear = () => {
        receiptNumber.current = Date.now().toString().slice(-6);
        setForm(emptyForm);
    };

    return (
        <Box sx={{ width: '100%', p: { xs: 2, md: 4 } }}>
            <Snackbar
                open={alert.open}
                autoHideDuration={3500}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                onClose={() => setAlert(a => ({ ...a, open: false }))}
            >
                <Alert severity={alert.type} sx={{ width: '100%' }}>{alert.msg}</Alert>
            </Snackbar>

            {/* Confirmar exclusão */}
            <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, id: null })}>
                <DialogTitle>Confirmar exclusão</DialogTitle>
                <DialogContent>
                    <Typography>Tem certeza que deseja excluir este recibo? Esta ação não pode ser desfeita.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm({ open: false, id: null })}>Cancelar</Button>
                    <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirm.id)}>Excluir</Button>
                </DialogActions>
            </Dialog>

            <Typography variant="h5" fontWeight={700} mb={3}>Gerar Recibo</Typography>

            <Grid container spacing={4}>
                {/* ─── Formulário ─── */}
                <Grid item xs={12} md={5}>
                    <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600} mb={2}>Emissor</Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="CNPJ do Emissor"
                                    value={form.emitterDoc}
                                    onChange={handleCNPJChange}
                                    placeholder="00.000.000/0001-00"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {searching
                                                    ? <CircularProgress size={18} />
                                                    : <SearchIcon color="action" fontSize="small" />}
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Nome / Razão Social do Emissor" value={form.emitterName} onChange={handleField('emitterName')} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField fullWidth label="Telefone (opcional)" value={form.emitterPhone} onChange={handleField('emitterPhone')} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField fullWidth label="Cidade" value={form.emitterCity} onChange={handleField('emitterCity')} />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />
                        <Typography variant="subtitle1" fontWeight={600} mb={2}>Pagamento</Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Valor (R$)"
                                    value={form.value}
                                    onChange={handleField('value')}
                                    placeholder="0,00"
                                    InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Pagador" value={form.payerName} onChange={handleField('payerName')} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth label="CPF / CNPJ do Pagador (opcional)" value={form.payerDoc} onChange={handleField('payerDoc')} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    label="Referente a"
                                    value={form.referente}
                                    onChange={handleField('referente')}
                                    placeholder="Ex: prestação de serviços de consultoria referente ao mês de janeiro/2026"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Forma de Pagamento</InputLabel>
                                    <Select label="Forma de Pagamento" value={form.paymentMethod} onChange={handleField('paymentMethod')}>
                                        {PAYMENT_METHODS.map(m => (
                                            <MenuItem key={m} value={m}>{m}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField fullWidth type="date" label="Data do Pagamento" value={form.paymentDate} onChange={handleField('paymentDate')} InputLabelProps={{ shrink: true }} />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />
                        <Typography variant="subtitle1" fontWeight={600} mb={2}>Observações</Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Texto livre / Observações"
                            value={form.observations}
                            onChange={handleField('observations')}
                            placeholder="Informe aqui observações adicionais que deseja incluir no recibo..."
                        />

                        <Box display="flex" gap={2} mt={3} flexWrap="wrap">
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={saving}
                                fullWidth
                            >
                                {saving ? 'Salvando...' : 'Salvar Recibo'}
                            </Button>
                            <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint} fullWidth>
                                Imprimir / PDF
                            </Button>
                            <Button variant="outlined" color="inherit" startIcon={<AddIcon />} onClick={handleClear} fullWidth>
                                Novo Recibo
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                {/* ─── Preview do Recibo ─── */}
                <Grid item xs={12} md={7}>
                    <Box ref={receiptRef}>
                        <ReciboPrint form={form} receiptNumber={receiptNumber.current} />
                    </Box>
                </Grid>
            </Grid>

            {/* ─── Lista de Recibos Salvos ─── */}
            <Box mt={6}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                    <Typography variant="h6" fontWeight={700}>Recibos Salvos</Typography>
                    <TextField
                        size="small"
                        label="Buscar"
                        placeholder="Pagador, emissor, CNPJ ou nº recibo..."
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            currentPage.current = 1;
                            loadReceipts(1, e.target.value);
                        }}
                        InputProps={{
                            endAdornment: <InputAdornment position="end"><SearchIcon fontSize="small" color="action" /></InputAdornment>
                        }}
                        sx={{ width: 320 }}
                    />
                </Box>

                <TableContainer component={Paper} elevation={2}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Nº</strong></TableCell>
                                <TableCell><strong>Data</strong></TableCell>
                                <TableCell><strong>Pagador</strong></TableCell>
                                <TableCell><strong>Emissor</strong></TableCell>
                                <TableCell><strong>Referente</strong></TableCell>
                                <TableCell align="right"><strong>Valor</strong></TableCell>
                                <TableCell><strong>Pagamento</strong></TableCell>
                                <TableCell align="center"><strong>Ações</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loadingList ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={28} />
                                    </TableCell>
                                </TableRow>
                            ) : receipts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                        Nenhum recibo encontrado
                                    </TableCell>
                                </TableRow>
                            ) : receipts.map((row) => {
                                const a = row.attributes;
                                return (
                                    <TableRow key={row.id} hover>
                                        <TableCell>{a.receipt_number}</TableCell>
                                        <TableCell>{a.payment_date ? a.payment_date.split('-').reverse().join('/') : '-'}</TableCell>
                                        <TableCell>{a.payer_name}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap maxWidth={180}>{a.emitter_name}</Typography>
                                            {a.emitter_doc && <Typography variant="caption" color="text.secondary">{a.emitter_doc}</Typography>}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap maxWidth={200}>{a.referente}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <strong>R$ {formatCurrency(String(a.value ?? '0'))}</strong>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={a.payment_method} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={0} justifyContent="center">
                                                <Tooltip title="Visualizar / Reimprimir">
                                                    <IconButton color="primary" size="small" onClick={() => handleLoad(row)}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Excluir">
                                                    <IconButton color="error" size="small" onClick={() => setDeleteConfirm({ open: true, id: row.id })}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Stack direction="row" justifyContent="flex-end" mt={2}>
                    <Pagination
                        count={pages.pageCount || 1}
                        page={currentPage.current}
                        color="primary"
                        onChange={(_, value) => {
                            currentPage.current = value;
                            loadReceipts(value, searchQuery);
                        }}
                    />
                </Stack>
            </Box>
        </Box>
    );
}

function ReciboPrint({ form, receiptNumber }) {
    const formattedDate = (() => {
        try {
            const [y, mo, d] = form.paymentDate.split('-');
            return `${d}/${mo}/${y}`;
        } catch {
            return form.paymentDate;
        }
    })();

    const valorFormatado = form.value ? `R$ ${formatCurrency(form.value)}` : 'R$ _______________';

    return (
        <Paper elevation={4} sx={{ p: 4, borderRadius: 2, fontFamily: 'Arial, sans-serif', fontSize: '14px', className: 'receipt-wrap' }}>
            {/* Cabeçalho */}
            <Box className="receipt-header" sx={{ textAlign: 'center', borderBottom: '2px solid #333', pb: 1.5, mb: 2 }}>
                <Typography variant="h5" fontWeight={800} letterSpacing={4} textTransform="uppercase">
                    Recibo
                </Typography>
                <Typography variant="caption" color="text.secondary">Nº {receiptNumber}</Typography>
            </Box>

            {/* Valor em destaque */}
            <Box className="clearfix" sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Box className="receipt-value-box" sx={{ border: '2px solid #333', px: 2.5, py: 1, fontWeight: 700, fontSize: '1.3rem' }}>
                    {valorFormatado}
                </Box>
            </Box>

            {/* Corpo */}
            <Box className="receipt-body" sx={{ lineHeight: 2.2 }}>
                <Typography variant="body1">
                    Recebi de{' '}
                    <Box component="span" sx={{ borderBottom: '1px solid #555', display: 'inline-block', minWidth: 220, px: 0.5, fontWeight: 600 }}>
                        {form.payerName || ''}
                    </Box>
                    {form.payerDoc && (
                        <>
                            {' '}portador do CPF/CNPJ{' '}
                            <Box component="span" sx={{ borderBottom: '1px solid #555', display: 'inline-block', minWidth: 140, px: 0.5 }}>
                                {form.payerDoc}
                            </Box>
                        </>
                    )}
                    {', '}a importância de{' '}
                    <Box component="span" sx={{ border: '1px solid #333', display: 'inline-block', px: 1, fontWeight: 700 }}>
                        {valorFormatado}
                    </Box>
                </Typography>

                <Typography variant="body1" mt={1}>
                    referente a{' '}
                    <Box component="span" sx={{ borderBottom: '1px solid #555', display: 'inline-block', minWidth: 300, px: 0.5 }}>
                        {form.referente || ''}
                    </Box>
                </Typography>

                <Typography variant="body1" mt={1}>
                    Forma de pagamento:{' '}
                    <Box component="span" sx={{ fontWeight: 600 }}>
                        {form.paymentMethod}
                    </Box>
                </Typography>

                {form.observations && (
                    <Box className="obs-block" sx={{ mt: 2, p: 1.5, backgroundColor: '#f9f9f9', borderRadius: 1, fontSize: '13px', color: '#444' }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{form.observations}</Typography>
                    </Box>
                )}
            </Box>

            <Divider sx={{ borderStyle: 'dashed', my: 3 }} />

            {/* Emissor e data */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="body2">
                        Emissor:{' '}
                        <Box component="span" sx={{ fontWeight: 600 }}>{form.emitterName || '___________________________'}</Box>
                    </Typography>
                    {form.emitterDoc && (
                        <Typography variant="body2">CNPJ: {form.emitterDoc}</Typography>
                    )}
                    {form.emitterPhone && (
                        <Typography variant="body2">Tel.: {form.emitterPhone}</Typography>
                    )}
                </Box>
                <Box textAlign="right">
                    <Typography variant="body2">
                        {form.emitterCity || '_________________'}, {formattedDate}
                    </Typography>
                </Box>
            </Box>

            {/* Assinatura */}
            <Box className="receipt-footer" sx={{ mt: 5, textAlign: 'center' }}>
                <Box sx={{ borderTop: '1px solid #333', width: 320, mx: 'auto', pt: 1 }}>
                    <Typography variant="caption">{form.emitterName || 'Assinatura do Emissor'}</Typography>
                    {form.emitterDoc && (
                        <Typography variant="caption" display="block">{form.emitterDoc}</Typography>
                    )}
                </Box>
            </Box>
        </Paper>
    );
}
